import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatApiError } from "@/lib/errors";

export default function AdminMatrix() {
  const { refresh } = useAuth();
  const [data, setData] = useState({ roles: [], apps: [] });
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // roleName -> Set(appIds)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/matrix");
      setData(data);
      const e = {};
      for (const r of data.roles) {
        e[r.name] = new Set(r.app_access || []);
      }
      setEdits(e);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggle = (roleName, appId) => {
    setEdits((prev) => {
      const copy = { ...prev };
      const s = new Set(copy[roleName] || []);
      if (s.has(appId)) s.delete(appId);
      else s.add(appId);
      copy[roleName] = s;
      return copy;
    });
  };

  const isDirty = useMemo(() => {
    return data.roles.some((r) => {
      if (r.is_locked) return false;
      const cur = edits[r.name] || new Set();
      const orig = new Set(r.app_access || []);
      if (cur.size !== orig.size) return true;
      for (const x of cur) if (!orig.has(x)) return true;
      return false;
    });
  }, [data, edits]);

  const save = async () => {
    setSaving(true);
    try {
      const dirty = data.roles.filter((r) => {
        if (r.is_locked) return false;
        const cur = edits[r.name] || new Set();
        const orig = new Set(r.app_access || []);
        if (cur.size !== orig.size) return true;
        for (const x of cur) if (!orig.has(x)) return true;
        return false;
      });
      for (const r of dirty) {
        await api.post("/admin/matrix", {
          role: r.name,
          app_access: Array.from(edits[r.name] || []),
        });
      }
      toast.success(`Matrix saved · ${dirty.length} role(s) updated`);
      await load();
      // Saving the matrix can change app_access for the current user's
      // own role — bump /auth/me so the Apps launcher and route guards
      // reflect it instantly.
      await refresh({ force: true });
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-matrix" className="mx-auto w-full max-w-6xl">
      <Breadcrumbs />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Permissions Matrix
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Toggle which apps each role can access. Super admin is always all apps.
          </p>
        </div>
        <Button
          disabled={!isDirty || saving}
          onClick={save}
          data-testid="matrix-save"
          className="gap-2 bg-cargodash-primary-600 hover:bg-cargodash-primary-700"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : isDirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : data.apps.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No apps registered yet — add one in the Apps page first.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="sticky left-0 z-10 border-r border-slate-200 bg-slate-50 px-4 py-2.5 text-left font-medium text-slate-600">
                    Role
                  </th>
                  {data.apps.map((a) => (
                    <th
                      key={a.id}
                      className="min-w-[140px] px-3 py-2.5 text-left font-medium text-slate-600"
                      title={a.name}
                    >
                      {a.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.roles.map((r) => {
                  const wild = (edits[r.name] || new Set()).has("*") || r.is_locked;
                  return (
                    <tr
                      key={r.name}
                      className="border-t border-slate-200"
                      data-testid={`matrix-row-${r.name}`}
                    >
                      <td className="sticky left-0 z-10 border-r border-slate-200 bg-white px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-slate-900">
                              {r.display_name || r.name}
                            </div>
                            <code className="text-[10px] text-slate-500">{r.name}</code>
                          </div>
                          {r.is_locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                        </div>
                      </td>
                      {data.apps.map((a) => {
                        const checked = wild || (edits[r.name] || new Set()).has(a.id);
                        return (
                          <td
                            key={a.id}
                            className="px-3 py-2.5"
                            data-testid={`matrix-cell-${r.name}-${a.id}`}
                            aria-disabled={r.is_locked || undefined}
                          >
                            <Checkbox
                              disabled={r.is_locked}
                              aria-disabled={r.is_locked || undefined}
                              checked={checked}
                              onCheckedChange={() => toggle(r.name, a.id)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-3 text-[11px] text-slate-400">
        Effective access for a user = intersection of user's own app_access and
        their role's app_access. Super admin bypasses the matrix entirely.
      </p>
    </div>
  );
}
